import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ExpandMore as ExpandIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Note as NoteIcon,
  CheckCircle as ApprovedIcon,
  Pending as PendingIcon
} from '@mui/icons-material';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';

function ConsultationDetailView({ token }) {
  const navigate = useNavigate();
  const { consultationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [consultation, setConsultation] = useState(null);

  const loadConsultation = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await clinicalSupervisorApi.getConsultation(consultationId, token);
      setConsultation(data.consultation);
    } catch (err) {
      console.error('Error loading consultation:', err);
      setError(err.response?.data?.msg || 'Failed to load consultation details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (consultationId) {
      loadConsultation();
    }
  }, [consultationId, token]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !consultation) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Consultation not found'}</Alert>
        <IconButton onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          <BackIcon /> Go Back
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate(-1)}>
          <BackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h5">Consultation Details</Typography>
          <Typography variant="body2" color="text.secondary">
            ID: {consultation._id}
          </Typography>
        </Box>
        <Chip
          label={consultation.status}
          color={
            consultation.status === 'completed'
              ? 'success'
              : consultation.status === 'in_progress'
              ? 'warning'
              : 'default'
          }
        />
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <PersonIcon /> Participants
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Client
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {consultation.clientId?.name || 'Unknown'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {consultation.clientId?.email}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Therapist
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {consultation.therapistId?.name || 'Unknown'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {consultation.therapistId?.email}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <CalendarIcon /> Session Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Started
                </Typography>
                <Typography variant="body1">
                  {formatDate(consultation.createdAt)}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
                <Typography variant="body1">
                  {consultation.completedAt ? formatDate(consultation.completedAt) : 'In Progress'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Messages
                </Typography>
                <Typography variant="body1">
                  {consultation.messages?.length || 0} messages
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Consultation Messages */}
      {consultation.messages && consultation.messages.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
            <NoteIcon /> Consultation Chat ({consultation.messages.length} messages)
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ maxHeight: '500px', overflowY: 'auto' }}>
            {consultation.messages.map((message, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: message.senderRole === 'therapist' ? 'primary.light' : 'grey.100',
                  borderLeft: 4,
                  borderColor: message.senderRole === 'therapist' ? 'primary.main' : 'secondary.main'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Chip
                    label={message.senderRole === 'therapist' ? 'Therapist' : 'AI Supervisor'}
                    size="small"
                    color={message.senderRole === 'therapist' ? 'primary' : 'secondary'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(message.timestamp)}
                  </Typography>
                </Box>
                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                  {message.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Summary */}
      {consultation.summary && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Consultation Summary
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body1" paragraph style={{ whiteSpace: 'pre-wrap' }}>
            {consultation.summary}
          </Typography>
        </Paper>
      )}

      {/* Key Points */}
      {consultation.keyPoints && consultation.keyPoints.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Key Points
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            {consultation.keyPoints.map((point, idx) => (
              <ListItem key={idx}>
                <ListItemText
                  primary={`${idx + 1}. ${point}`}
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Care Notes */}
      {consultation.careNotes && consultation.careNotes.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
            <NoteIcon /> Care Notes ({consultation.careNotes.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {consultation.careNotes.map((note, idx) => (
            <Accordion key={note._id || idx}>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Typography flex={1}>{note.type || 'Care Note'}</Typography>
                  <Chip
                    icon={note.active ? <ApprovedIcon /> : <PendingIcon />}
                    label={note.active ? 'Approved' : 'Pending Review'}
                    size="small"
                    color={note.active ? 'success' : 'warning'}
                  />
                  <Chip label={note.priority} size="small" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="body1" paragraph style={{ whiteSpace: 'pre-wrap' }}>
                    {note.content}
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Applicable to:
                      </Typography>
                      <Box display="flex" gap={0.5} mt={0.5}>
                        {note.applicableToAI && (
                          <Chip label="AI Therapist" size="small" variant="outlined" />
                        )}
                        {note.applicableToHuman && (
                          <Chip label="Human Therapist" size="small" variant="outlined" />
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Created by:
                      </Typography>
                      <Typography variant="body2">
                        {note.createdBy === 'ai_suggestion' ? 'AI Suggestion' : 'Therapist'}
                      </Typography>
                      {note.createdAt && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatDate(note.createdAt)}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  {note.approvedBy && (
                    <Box mt={2} p={2} bgcolor="success.light" borderRadius={1}>
                      <Typography variant="body2" color="success.dark">
                        Approved by Clinical Supervisor on {formatDate(note.approvedAt)}
                      </Typography>
                    </Box>
                  )}

                  {note.rejectedBy && (
                    <Box mt={2} p={2} bgcolor="error.light" borderRadius={1}>
                      <Typography variant="body2" color="error.dark" gutterBottom>
                        Rejected on {formatDate(note.rejectedAt)}
                      </Typography>
                      {note.rejectionReason && (
                        <Typography variant="body2">Reason: {note.rejectionReason}</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}

      {/* Recommendations */}
      {consultation.recommendations && consultation.recommendations.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recommendations
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            {consultation.recommendations.map((recommendation, idx) => (
              <ListItem key={idx}>
                <ListItemText
                  primary={`${idx + 1}. ${recommendation}`}
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}

export default ConsultationDetailView;
